<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import LpgLeftArrow from '@icons/LpgLeftArrow.svelte';
	import Icon from '@components/Icon.svelte';
	import { de } from 'date-fns/locale';
	import LpgRightArrow from '@icons/LpgRightArrow.svelte';
	import DayButton from './DayButton.svelte';
	import { generateCalendarData } from './calendar';
	import { addMonths, format, getMonth, getYear, isBefore, subMonths } from 'date-fns';
	import type { PickerDateRange } from './types';

	const dispatch = createEventDispatcher<{ select: PickerDateRange }>();

	export let selectedRange: PickerDateRange = {
		start: undefined,
		end: undefined
	};
	export let previewDate: Date | undefined = undefined;
	const weekdays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

	let viewDate = new Date();

	$: calendarGrid = generateCalendarData(getYear(viewDate), getMonth(viewDate));

	function nextMonth() {
		viewDate = addMonths(viewDate, 1);
	}

	function prevMonth() {
		viewDate = subMonths(viewDate, 1);
	}

	function handleRangeSelect(date: Date) {
		if (selectedRange.start && !selectedRange.end && isBefore(date, selectedRange.start)) {
			const newEnd = selectedRange.start;
			selectedRange = {
				start: date,
				end: newEnd
			};
		} else if (selectedRange.start && selectedRange.end) {
			selectedRange = { start: date, end: undefined };
		} else if (selectedRange.start) {
			selectedRange.end = date;
		} else {
			selectedRange.start = date;
		}
		dispatch('select', selectedRange);
	}

	function generateAriaLabel(date: Date) {
		const formattedDate = format(date, 'EEEE, dd MMMM yyyy', { locale: de });
		if (!selectedRange.start || date < selectedRange.start || selectedRange.end) {
			return `Erstes Datum auswählen, ${formattedDate}`;
		}
		if (!selectedRange.end) {
			return `Zweites Datum auswählen, ${formattedDate}`;
		}
		return `Ausgewählte Zeitspanne ändern, aktuell von ${format(selectedRange.start, 'EEEE, dd MMMM yyyy', { locale: de })} 
        bis ${format(selectedRange.end, 'EEEE, dd MMMM yyyy', { locale: de })}`;
	}
</script>

<div
	class="horizontal-alignment z-20 absolute w-full pt-5.25 px-3 pb-3.75 mt-[0.1875rem] max-w-xs bg-white border rounded border-gray-4"
	aria-label="Datumsbereich auswählen"
>
	<div aria-live="polite" class="sr-only">
		{selectedRange.start
			? `Erstes Datum ausgewählt: ${format(selectedRange.start, 'EEEE, dd MMMM yyyy', { locale: de })}`
			: ''}
		{selectedRange.end
			? `, Zweites Datum ausgewählt: ${format(selectedRange.end, 'EEEE, dd MMMM yyyy', { locale: de })}`
			: ''}
	</div>
	<div class="px-2 mb-4.5 flex items-center justify-between">
		<button on:click={prevMonth} aria-label="Zum vorherigen Monat">
			<Icon class="!w-5.25 !h-5.25" component={LpgLeftArrow} />
		</button>
		<span>
			{format(viewDate, 'MMMM yyyy', { locale: de })}
		</span>
		<button on:click={nextMonth} aria-label="Zum nächsten Monat">
			<Icon class="!w-5.25 !h-5.25" component={LpgRightArrow} />
		</button>
	</div>

	<table class="w-full relative border-collapse box-border">
		<thead>
			<tr>
				{#each weekdays as weekday}
					<th class="text-copy font-normal px-0.75 pb-2.25" scope="col">
						{weekday}
					</th>
				{/each}
			</tr>
		</thead>
		<tbody>
			{#each calendarGrid as weekRow}
				<tr>
					{#each weekRow as day}
						{#if day.isCurrentMonthDate}
							<td class="border-0 p-0 m-0 text-center align-middle relative">
								{#key selectedRange}
									<DayButton
										date={day.date}
										{selectedRange}
										{previewDate}
										on:click={() => handleRangeSelect(day.date)}
										on:mouseenter={() => (previewDate = day.date)}
										on:mouseleave={() => (previewDate = undefined)}
										on:focus={() => (previewDate = day.date)}
										on:blur={() => (previewDate = undefined)}
										aria-label={generateAriaLabel(day.date)}
									/>
								{/key}
							</td>
						{:else}
							<td></td>
						{/if}
					{/each}
				</tr>
			{/each}
		</tbody>
	</table>
</div>

<style>
	.horizontal-alignment {
		margin-left: min(calc((100vw - (320px + 24px)) / 2), 0px) !important;
	}
</style>
